import { useInfiniteQuery } from '@tanstack/react-query';

import { useIsAuthenticated } from '../context/UserContext';
import { unwrapFromResult } from '../helpers/resultWrapper';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import { CONVERSATIONS_KEY } from '../queryKeys';

// Conversations are loaded ten at a time; a shorter page is the last.
const CONVERSATIONS_PAGE_SIZE = 10;

/**
 * The conversation list query. Main mirrors its pages into the conversations
 * store (where sockets keep them live); the list UIs call this too for paging
 * (hasNextPage, fetchNextPage, isLoading). React Query shares one query
 * between them, so the paging state lives in one place instead of being
 * copied into context on every change.
 */
const useConversationsQuery = (client: WrappedTRPCClient) => {
  const isAuthenticated = useIsAuthenticated();
  return useInfiniteQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: ({ pageParam }) =>
      unwrapFromResult(client.messages.get.query({ cursor: pageParam })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const conversations = lastPage?.conversations ?? [];
      if (conversations.length < CONVERSATIONS_PAGE_SIZE) return undefined;
      return conversations[conversations.length - 1]?.id;
    },
    enabled: Boolean(isAuthenticated)
  });
};

export default useConversationsQuery;
